import React, { useRef, useState, useEffect, useLayoutEffect, useMemo, useReducer } from 'react';
import { flushSync } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { SketchPicker } from 'react-color';
import { Button, Modal, Popover, Dropdown, Switch, Slider , Tooltip } from 'antd';
import { UndoOutlined, RedoOutlined, EyeInvisibleOutlined, EyeOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { handleCreateEbookHighlights, handleListUserEbooklights } from "store/ebook_highlighter/thunks";
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { useDispatch } from 'react-redux';
import { handleNonNativeEbookListHighlights } from 'store/ebookNonNativeImageHighLighter/thunks';
import { DefaultAudioPlayer, DefaultVideoPlayer } from './UI';
import { combineRectangles, disableBodyScroll } from 'utils/methods/math';
import { useFullScreen } from 'utils/context/FullScreenProvider';
import useOrientationFullScreen from './hooks/useOrientationFullScreen';
import * as Sentry from '@sentry/react';
import Loading from './Loading';
import { useAlert } from './UI/alertSakeBar/alertProvider';
import SentryWrap from 'components/Sentry';

// Styled Components  
const ImageHighlighterDefinitionPopoverStyle = createGlobalStyle`
  .image-highlighter-definition-popover.ant-popover .ant-popover-title {
    margin-bottom: 0 !important;
  }
`;

const CanvasContainer = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  cursor: ${({ cursorType }) => cursorType || 'default'};
`;

const Image = styled.img`
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  height: auto;
  width: 100%;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  margin-bottom: 30px;
  @media (max-width: 432px){
    margin-bottom: 70px;
  }
`;

const ControlsContainer = styled.div`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  flex-wrap: wrap;
  padding: 10px;
  background-color: #fff;
  @media print {
    display: none;
  }
`;

const LineWidthPreview = styled.div`
  width: 100px;
  height: ${({ strokeWidth }) => strokeWidth}px;
  background-color: ${({ color }) => (color ? color : 'black')};
  margin-top: ${({ top }) => top}px;
`;

// Utility functions
const highlighterModeStorageKey = (b, p) =>
  b != null && p != null ? `isHighlighterMode_${b}_${p}` : null;

const readHighlighterModeForPage = (b, p) => {
  const k = highlighterModeStorageKey(b, p);
  if (!k) return sessionStorage.getItem('isHighlighterMode') === 'true';
  const v = sessionStorage.getItem(k);
  if (v !== null) return v === 'true';
  return false;
};

const writeHighlighterModeForPage = (b, p, checked) => {
  const k = highlighterModeStorageKey(b, p);
  if (k) sessionStorage.setItem(k, String(checked));
};

const undoRedoPageKey = (b, p) => `${String(b)}_${String(p)}`;

const cloneRectangleList = (list) => {
  if (!Array.isArray(list)) return [];
  try {
    return structuredClone(list);
  } catch {
    return list.map((r) => (r && typeof r === 'object' ? { ...r } : r));
  }
};

const cloneRectangleSnapshotsStack = (stack) => {
  if (!Array.isArray(stack)) return [];
  return stack.map((snap) => cloneRectangleList(snap));
};

const createInitialEditor = () => ({
  rectangles: [],
  history: [],
  redoHistory: [],
});

// Reducer function
function editorReducer(state, action) {
  switch (action.type) {
    case 'REPLACE_EDITOR':
      return action.next;
    case 'SET_RECTANGLES':
      return { ...state, rectangles: action.payload };
    case 'SET_PAGE_STACKS':
      return {
        ...state,
        history: action.history,
        redoHistory: action.redoHistory,
      };
    case 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO':
      return {
        ...state,
        history: [...state.history, cloneRectangleList(state.rectangles)],
        rectangles: action.rectangles,
        redoHistory: [],
      };
    case 'PUSH_HISTORY_SET_RECTS':
      return {
        ...state,
        history: [...state.history, cloneRectangleList(state.rectangles)],
        rectangles: action.rectangles,
      };
    case 'UNDO': {
      if (state.history.length === 0) return state;
      const previousState = state.history[state.history.length - 1];
      return {
        rectangles: cloneRectangleList(previousState),
        history: state.history.slice(0, -1),
        redoHistory: [cloneRectangleList(state.rectangles), ...state.redoHistory],
      };
    }
    case 'REDO': {
      if (state.redoHistory.length === 0) return state;
      const nextState = state.redoHistory[0];
      return {
        rectangles: cloneRectangleList(nextState),
        history: [...state.history, cloneRectangleList(state.rectangles)],
        redoHistory: state.redoHistory.slice(1),
      };
    }
    default:
      return state;
  }
}

const ImageHighlighter = ({ imageUrl, pageId, bookId, setImageConfiguration, isNonNative=false, ebookFlagTeacherEdition = false }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const lastPageContextRef = useRef(null);
  const historyByPageRef = useRef(new Map());
  const redoByPageRef = useRef(new Map());
  const undoActionBusyRef = useRef(false);
  const redoActionBusyRef = useRef(false);
  const skipEbookRectanglesUntilRef = useRef(0);
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const [editor, dispatchEditor] = useReducer(
    editorReducer,
    undefined,
    createInitialEditor
  );
  const { rectangles, history, redoHistory } = editor;
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const [newRect, setNewRect] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState(sessionStorage.getItem('selectedColor') || '#50e3c2');
  const [strokeWidth, setStrokeWidth] = useState(Number(sessionStorage.getItem('strokeWidth')) || 2);
  const [opacity, setOpacity] = useState(Math.max(Number(sessionStorage.getItem('opacity')) || 0.4, 0.4));
  const [naturalDimensions, setNaturalDimensions] = useState({ width: 1, height: 1 });
  const [isHighlighterMode, setIsHighlighterMode] = useState(() =>
    readHighlighterModeForPage(bookId, pageId)
  );
  const [isAnnotationsVisible, setIsAnnotationsVisible] = useState(false);
  const [hoveredRectIndex, setHoveredRectIndex] = useState(null);
  const dispatch = useDispatch();
  const [ebook_highlights,setAllHighlightData] = useState([]);
  const [highlights, setHighlights] = useState({});
  const [is_image_loaded, setIsImageLoaded] = useState(false);
  const [enableHighLighterEditMode,setEnableHighLighterEditMode] = useState(false);
  const [enableDrawerTool,setEnableDrawerTool] = useState(sessionStorage.getItem('isDrawingMode') === 'true');
  const [enableMove,setEnableMove] = useState(sessionStorage.getItem('isMoveMode') === 'true');
  const [enableResize,setEnableResize] = useState(sessionStorage.getItem('isResizeMode') === 'true');
  const [ deleteOnIpad, setDeleteOnIpad ] = useState(false);
  const [movingRectIndex, setMovingRectIndex] = useState(null);
  const [resizingRectIndex, setResizingRectIndex] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [tempRectTransform, setTempRectTransform] = useState(null);
  const [openVideoModal,setVideoModal] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [openAudioPlayer, setOpenAudioPlayer] = useState(false);
  const [definitionText, setDefinitionText] = useState('');
  const [definitionWord, setDefinitionWord] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [openTooltipIndex, setOpenTooltipIndex] = useState(null);
  const [isPointer,setIsPointer] = useState(false);
  const [cursorType, setCursorType] = useState('default');
  const [videoHighliters,setVideoHighlites] = useState([]);
  const [userHighlites, setUserHighlites] = useState([]);
  const { currentFullScreen } = useFullScreen();
  const landscapeOrientation = useOrientationFullScreen();
  const showAlert = useAlert();

  const fetchData = async () => {
    if (bookId) {
      const [nonNative, native] =  await Promise.all([
        dispatch(handleNonNativeEbookListHighlights(bookId)),
        dispatch(
          handleListUserEbooklights(currentUser.id, bookId, currentClass.id)
        ),
      ]);
      setVideoHighlites(nonNative);
      setUserHighlites(native);
      setAllHighlightData([...(Array.isArray(nonNative) ? nonNative : []), ...(Array.isArray(native) ? native : [])]);
    }
  }

  useEffect(() => {
    if (bookId && currentUser?.id) {
      fetchData()
    }
  }, [bookId, currentUser]);

  useLayoutEffect(() => {
    const prev = lastPageContextRef.current;
    const hasPage = bookId != null && pageId != null;
    const nextKey = hasPage ? undoRedoPageKey(bookId, pageId) : null;

    if (
      prev != null &&
      prev.bookId != null &&
      prev.pageId != null &&
      hasPage
    ) {
      const prevKey = undoRedoPageKey(prev.bookId, prev.pageId);
      if (prevKey !== nextKey) {
        historyByPageRef.current.set(
          prevKey,
          cloneRectangleSnapshotsStack(editorRef.current.history)
        );
        redoByPageRef.current.set(
          prevKey,
          cloneRectangleSnapshotsStack(editorRef.current.redoHistory)
        );
      }
    }

    if (nextKey) {
      const savedH = historyByPageRef.current.get(nextKey);
      const savedR = redoByPageRef.current.get(nextKey);
      flushSync(() => {
        dispatchEditor({
          type: 'SET_PAGE_STACKS',
          history: savedH ? cloneRectangleSnapshotsStack(savedH) : [],
          redoHistory: savedR ? cloneRectangleSnapshotsStack(savedR) : [],
        });
      });
    } else {
      flushSync(() => {
        dispatchEditor({
          type: 'SET_PAGE_STACKS',
          history: [],
          redoHistory: [],
        });
      });
    }

    setIsHighlighterMode(readHighlighterModeForPage(bookId, pageId));
    const navigated =
      prev != null &&
      (String(prev.bookId) !== String(bookId) ||
        String(prev.pageId) !== String(pageId));
    lastPageContextRef.current = { bookId, pageId };
    if (navigated) {
      flushSync(() => {
        dispatchEditor({ type: 'SET_RECTANGLES', payload: [] });
      });
      setEnableMove(false);
      setEnableResize(false);
      setDeleteOnIpad(false);
      sessionStorage.setItem('isMoveMode', 'false');
      sessionStorage.setItem('isResizeMode', 'false');
    }
  }, [pageId, bookId]);

  useEffect(() => {
    try{
      if (bookId && pageId && ebook_highlights) {
        const bookHighlights = Array.isArray(ebook_highlights)
          ? ebook_highlights.filter((h) => String(h.book_id) === String(bookId))
          : [];
        if (Array.isArray(bookHighlights)) {
          const page_item = bookHighlights.find(h => Number(h.page_id) === Number(pageId)) || null;
          const filter_page_item = bookHighlights.filter(h => Number(h.page_id) === Number(pageId));
          const combinedRectangle = combineRectangles(filter_page_item)?.rectangles?.length >0 ?  combineRectangles(filter_page_item)?.rectangles : [];
          const applyIncomingRects = (incoming) => {
            const inc = Array.isArray(incoming) ? incoming : [];
            if (Date.now() < skipEbookRectanglesUntilRef.current) return;
            const local = editorRef.current;
            if (
              local.rectangles.length > 0 &&
              inc.length === 0 &&
              (local.history.length > 0 || local.redoHistory.length > 0)
            ) {
              return;
            }
            dispatchEditor({ type: 'SET_RECTANGLES', payload: inc });
          };
          if (page_item) {
            const mergedPageItem = {
              ...page_item,
              data: {
                ...(page_item.data || {}),
                rectangles: combinedRectangle,
              },
            };
            if (Object.keys(highlights)?.length > 0 && mergedPageItem?.data?.rectangles?.length === highlights?.data?.rectangles?.length && highlights.page_id === mergedPageItem.page_id && highlights.book_id === mergedPageItem.book_id) {
              return;
            }
            
            setHighlights(mergedPageItem);

            if (currentUser.role === 'admin') {
              setIsAnnotationsVisible(false);
              setEnableHighLighterEditMode(false);
            } else{
              if (!enableDrawerTool) {
                setIsAnnotationsVisible(mergedPageItem?.show);
                setEnableHighLighterEditMode(mergedPageItem?.show);
              }
            }

            if (Array.isArray(mergedPageItem.data.rectangles)) {
                mergedPageItem.data.rectangles.forEach(data => {
                  if (data?.non_native&& currentUser.id !== mergedPageItem.userId) {
                    data.color = "transparent";  
                    data.isHighlighterMode = false;
                }
                  return data;
              });
              applyIncomingRects(mergedPageItem.data.rectangles || []);
              setNaturalDimensions(mergedPageItem.data.naturalDimensions || { width: 1, height: 1 });
            } else {
              applyIncomingRects([]);
              setHighlights({
                page_id: pageId,
                book_id: bookId,
                data: {
                  rectangles: [],
                  naturalDimensions: { width: 1, height: 1 },
                },
              });
            }
          } else {
            applyIncomingRects([]);
            setHighlights({
              page_id: pageId,
              book_id: bookId,
              data: {
                rectangles: [],
                naturalDimensions: { width: 1, height: 1 },
              },
            });
            const canvas = canvasRef.current;
            const image = imageRef.current;
            if (canvas && image) {
              setNaturalDimensions({
                width: image.naturalWidth,
                height: image.naturalHeight,
              });
            }
          }
        }
      }
    }catch(error){
      console.log(error);
      Sentry.captureException(`221 Error in ImageHighlighter useEffect: ${error}`);
    }

  }, [ebook_highlights, pageId, bookId]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const image = imageRef.current;
      if (canvas && image && image.naturalWidth && image.naturalHeight) {
        canvas.width = image.clientWidth;
        canvas.height = image.clientHeight;
        setImageConfiguration({
          width: image.naturalWidth,
          height: image.naturalHeight,
          clientWidth: image.clientWidth,
          clientHeight: image.clientHeight,
        });
        const imageNaturalDimensions = {
          width: image.naturalWidth,
          height: image.naturalHeight,
        };
        if ((naturalDimensions && (naturalDimensions.width <= 1 || naturalDimensions.height <= 1)) || !naturalDimensions) {
          setNaturalDimensions(imageNaturalDimensions);
        }
        redrawCanvas(naturalDimensions && naturalDimensions.width > 1 && naturalDimensions.height > 1 ? naturalDimensions : imageNaturalDimensions);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [rectangles, isAnnotationsVisible, landscapeOrientation, currentFullScreen]);

  const getRelativePosition = (event) => {
  try {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return { x: 0, y: 0 };
    }
    return {
      x: ((event.clientX - rect.left) / rect.width) * (naturalDimensions.width || 1),
      y: ((event.clientY - rect.top) / rect.height) * (naturalDimensions.height || 1),
    };
  } catch (error) {
    // Sentry.captureException(`222 Error in getRelativePosition: ${error}`);
    return { x: 0, y: 0 };
  }
  
  };

  const getTouchRelativePosition = (touch) => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        return { x: 0, y: 0 };
      }
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return { x: 0, y: 0 };
      }
      return {
        x: ((touch.clientX - rect.left) / rect.width) * naturalDimensions.width,
        y: ((touch.clientY - rect.top) / rect.height) * naturalDimensions.height, 
      };
    } catch (error) {
      return { x: 0, y: 0 };
    }
  };

  const disableScroll = () => {
    if (window.innerWidth <= 1380) {
      document.documentElement.style.overflow = 'hidden';
      // document.body.style.overflow = 'hidden';
      disableBodyScroll('ImageHighlighter');
    }
  };

  const enableScroll = () => {
    if (window.innerWidth <= 1380) {
      if (currentFullScreen && !landscapeOrientation) {
        return;
      }
      else {
        document.body.style.overflow = 'auto';
      }
    }
  };

  const checkOverlap = (rect1, existingRectangles) => {
    const MARGIN = 2;
    
    const normalizedRect1 = {
      x: rect1.width < 0 ? rect1.x + rect1.width : rect1.x,
      y: rect1.height < 0 ? rect1.y + rect1.height : rect1.y,
      width: Math.abs(rect1.width),
      height: Math.abs(rect1.height)
    };

    const r1 = {
      left: normalizedRect1.x - MARGIN,
      right: normalizedRect1.x + normalizedRect1.width + MARGIN,
      top: normalizedRect1.y - MARGIN,
      bottom: normalizedRect1.y + normalizedRect1.height + MARGIN
    };

    const nativeRectangles = existingRectangles.filter(rect => !rect.non_native);

    return nativeRectangles.some(rect2 => {
      const normalizedRect2 = {
        x: rect2.width < 0 ? rect2.x + rect2.width : rect2.x,
        y: rect2.height < 0 ? rect2.y + rect2.height : rect2.y,
        width: Math.abs(rect2.width),
        height: Math.abs(rect2.height)
      };

      const r2 = {
        left: normalizedRect2.x - MARGIN,
        right: normalizedRect2.x + normalizedRect2.width + MARGIN,
        top: normalizedRect2.y - MARGIN,
        bottom: normalizedRect2.y + normalizedRect2.height + MARGIN
      };

      return !(r2.left > r1.right || 
              r2.right < r1.left || 
              r2.top > r1.bottom ||
              r2.bottom < r1.top);
    });
  };

  const clampRectToImageBounds = (rect) => {
    const maxW = naturalDimensions.width;
    const maxH = naturalDimensions.height;
    const minX = Math.min(rect.x, rect.x + rect.width);
    const minY = Math.min(rect.y, rect.y + rect.height);
    let normW = Math.abs(rect.width);
    let normH = Math.abs(rect.height);
    normW = Math.min(normW, maxW);
    normH = Math.min(normH, maxH);
    let clampedMinX = Math.max(0, Math.min(minX, maxW - normW));
    let clampedMinY = Math.max(0, Math.min(minY, maxH - normH));
    normW = Math.min(normW, maxW - clampedMinX);
    normH = Math.min(normH, maxH - clampedMinY);
    const newX = rect.width >= 0 ? clampedMinX : clampedMinX + normW;
    const newY = rect.height >= 0 ? clampedMinY : clampedMinY + normH;
    const newW = rect.width >= 0 ? normW : -normW;
    const newH = rect.height >= 0 ? normH : -normH;
    return { ...rect, x: newX, y: newY, width: newW, height: newH };
  };

  const MOVE_RESIZE_EPS_PX = 3;

  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !is_image_loaded || naturalDimensions.width <= 1 || naturalDimensions.height <= 1) {
      return;
    }
    
    const { x, y } = getRelativePosition(e);
    const rectIndex = checkContainerIndex(x, y);
    
    if (enableDrawerTool && rectIndex !== -1 && !rectangles[rectIndex]?.non_native) {  
      if (enableResize) {
        setResizingRectIndex(rectIndex);
        setDragStart({ x, y, clientX: e.clientX, clientY: e.clientY });
        setTempRectTransform(null);
        disableScroll();
        return;
      }
      
      if (enableMove) {
        setMovingRectIndex(rectIndex);
        setDragStart({ x, y, clientX: e.clientX, clientY: e.clientY });
        setTempRectTransform(null);
        disableScroll();
        return;
      }
    }
    
    if(!enableDrawerTool){
      return;
    }
    
    if(enableMove || enableResize){
      return;
    }
    
    if (hoveredRectIndex !== null) return;
    setIsDrawing(true);
    disableScroll();
    setNewRect({ x, y, width: 0, height: 0, color: selectedColor, strokeWidth, isHighlighterMode, opacity });
  };
  const checkContainerIndex = (x, y) => {
    const hoveredIndex = rectangles.findIndex((rect) => {
      const rectX1 = Math.min(rect.x, rect.x + rect.width);
      const rectX2 = Math.max(rect.x, rect.x + rect.width);
      const rectY1 = Math.min(rect.y, rect.y + rect.height);
      const rectY2 = Math.max(rect.y, rect.y + rect.height);
      return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
    });
    return hoveredIndex;
  }
  const handleMouseMove = (e) => {
    const { x, y } = getRelativePosition(e);
    
    if (movingRectIndex !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      const dxClient =
        typeof dragStart.clientX === 'number' ? e.clientX - dragStart.clientX : 999999;
      const dyClient =
        typeof dragStart.clientY === 'number' ? e.clientY - dragStart.clientY : 999999;
      const movedEnough =
        Math.abs(dxClient) > MOVE_RESIZE_EPS_PX || Math.abs(dyClient) > MOVE_RESIZE_EPS_PX;

      setCursorType('move');
      if (!movedEnough) return;
      setTempRectTransform({
        type: 'move',
        index: movingRectIndex,
        dx,
        dy
      });
      return;
    }
    
    if (resizingRectIndex !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      const dxClient =
        typeof dragStart.clientX === 'number' ? e.clientX - dragStart.clientX : 999999;
      const dyClient =
        typeof dragStart.clientY === 'number' ? e.clientY - dragStart.clientY : 999999;
      const movedEnough =
        Math.abs(dxClient) > MOVE_RESIZE_EPS_PX || Math.abs(dyClient) > MOVE_RESIZE_EPS_PX;

      setCursorType('nwse-resize');
      if (!movedEnough) return;
      setTempRectTransform({
        type: 'resize',
        index: resizingRectIndex,
        dx,
        dy
      });
      return;
    }
    
    if (isDrawing) {
      const updatedRect = {
        ...newRect,
        width: x - newRect.x,
        height: y - newRect.y
      };
      const clampedRect = clampRectToImageBounds(updatedRect);
      if (!checkOverlap(clampedRect, rectangles)) {
        setNewRect(clampedRect);
      }
      
      if (!enableMove && !enableResize) {
        setCursorType('crosshair');
      }
    } else {
      const hoveredIndex = Array.isArray(rectangles) ? rectangles.findIndex((rect) => {
        const rectX1 = Math.min(rect.x, rect.x + rect.width);
        const rectX2 = Math.max(rect.x, rect.x + rect.width);
        const rectY1 = Math.min(rect.y, rect.y + rect.height);
        const rectY2 = Math.max(rect.y, rect.y + rect.height);
    
        return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
      }) : -1;
  
      const hoveredRect = rectangles[hoveredIndex];
      
      if (hoveredRect?.non_native) {
        setIsPointer(true);
        setHoveredRectIndex(hoveredIndex);
        setCursorType('pointer');
      } else {
        if (enableHighLighterEditMode || enableDrawerTool) {
          setIsPointer(hoveredIndex !== -1);
          setHoveredRectIndex(hoveredIndex !== -1 ? hoveredIndex : null);
          
          if (hoveredIndex !== -1) {
            if (enableDrawerTool) {
              if (enableResize) {
                setCursorType('nwse-resize');
              } else if (enableMove) {
                setCursorType('move');
              } else if (!enableMove && !enableResize) {
                setCursorType('pointer');
              } else {
                setCursorType('default');
              }
            } else if (enableHighLighterEditMode) {
              setCursorType('pointer');
            } else {
              setCursorType('default');
            }
          } else {
            if (enableDrawerTool && !enableMove && !enableResize) {
              setCursorType('crosshair');
            } else {
              setCursorType('default');
            }
          }
        } else {
          setIsPointer(false);
          setHoveredRectIndex(null);
          setCursorType('default');
        }
      }
  
      if (hoveredIndex === -1) {
        setIsPointer(false);
        setHoveredRectIndex(null);
        if (enableDrawerTool && !enableMove && !enableResize) {
          setCursorType('crosshair');
        } else {
          setCursorType('default');
        }
      }
    }
  };

  const handleMouseUp = () => {
    if (movingRectIndex !== null) {
      if (tempRectTransform) {
      enableScroll();
      const updatedRectangles = editorRef.current.rectangles.map((rect, idx) => {
        if (idx === movingRectIndex) {
          const movedRect = {
            ...rect,
            x: rect.x + tempRectTransform.dx,
            y: rect.y + tempRectTransform.dy
          };
          return clampRectToImageBounds(movedRect);
        }
        return rect;
      });
      flushSync(() => {
        dispatchEditor({
          type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
          rectangles: updatedRectangles,
        });
      });
      SaveNativeHighlighter(updatedRectangles);
      }

      enableScroll();
      setMovingRectIndex(null);
      setDragStart(null);
      setTempRectTransform(null);
      return;
    }
    
    if (resizingRectIndex !== null) {
      if (tempRectTransform) {
      enableScroll();
      const updatedRectangles = editorRef.current.rectangles.map((rect, idx) => {
        if (idx === resizingRectIndex) {
          const { dx, dy } = tempRectTransform;
          const newRect = { ...rect };
          
          newRect.width = rect.width + dx;
          newRect.height = rect.height + dy;
          
          if (Math.abs(newRect.width) < 20) {
            newRect.width = rect.width;
          }
          if (Math.abs(newRect.height) < 20) {
            newRect.height = rect.height;
          }
          
          return clampRectToImageBounds(newRect);
        }
        return rect;
      });
      flushSync(() => {
        dispatchEditor({
          type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
          rectangles: updatedRectangles,
        });
      });
      SaveNativeHighlighter(updatedRectangles);
      }

      enableScroll();
      setResizingRectIndex(null);
      setResizeHandle(null);
      setDragStart(null);
      setTempRectTransform(null);
      return;
    }
    
    if(!enableDrawerTool){
      return;
    }
    if (isDrawing) {
      enableScroll();
      if (newRect.width !== 0 && newRect.height !== 0) {
        const absWidth = Math.abs(newRect.width);
        const absHeight = Math.abs(newRect.height);
        if (absWidth >= 20 && absHeight >= 20) {
          const base = editorRef.current.rectangles;
          const updatedRectangles = [...base, newRect];
          flushSync(() => {
            dispatchEditor({
              type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
              rectangles: updatedRectangles,
            });
          });
          SaveNativeHighlighter(updatedRectangles);
        }
      }
      setNewRect(null);
      setIsDrawing(false);
    }
  };

  useEffect(() => {
    if (movingRectIndex === null && resizingRectIndex === null) return;

    const onGlobalMouseUp = (e) => {
      const canvas = canvasRef.current;
      if (canvas && e?.target && canvas.contains(e.target)) return;
      handleMouseUp();
    };

    window.addEventListener('mouseup', onGlobalMouseUp);
    return () => window.removeEventListener('mouseup', onGlobalMouseUp);
  }, [movingRectIndex, resizingRectIndex, tempRectTransform, handleMouseUp]);

  const handleTouchStart = (e) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !is_image_loaded || naturalDimensions.width <= 1 || naturalDimensions.height <= 1) {
      return;
    }
    
    const { x, y } = getTouchRelativePosition(e.touches[0]);
    const rectIndex = checkContainerIndex(x, y);
    
    if (enableDrawerTool && rectIndex !== -1 && !rectangles[rectIndex]?.non_native) {
      if (enableResize) {
        setResizingRectIndex(rectIndex);
        setDragStart({ x, y });
        disableScroll();
        return;
      }
      
      if (enableMove) {
        setMovingRectIndex(rectIndex);
        setDragStart({ x, y });
        disableScroll();
        return;
      }
    }
    
    if(!enableDrawerTool){
      return;
    }
    
    if(enableMove || enableResize){
      return;
    }
    
    if (hoveredRectIndex !== null) return;
    setIsDrawing(true);
    disableScroll();
    setNewRect({ x, y, width: 0, height: 0, color: selectedColor, strokeWidth, isHighlighterMode, opacity });
  };

  const handleTouchMove = (e) => {
    const { x, y } = getTouchRelativePosition(e.touches[0]);
    
    if (movingRectIndex !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      
      setTempRectTransform({
        type: 'move',
        index: movingRectIndex,
        dx,
        dy
      });
      return;
    }
    
    if (resizingRectIndex !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      
      setTempRectTransform({
        type: 'resize',
        index: resizingRectIndex,
        dx,
        dy
      });
      return;
    }
    
    if(!enableDrawerTool){
      return;
    }
    if (isDrawing && newRect) {
      const updatedRect = {
        ...newRect,
        width: x - newRect.x,
        height: y - newRect.y
      };
      const clampedRect = clampRectToImageBounds(updatedRect);
      if (!checkOverlap(clampedRect, rectangles)) {
        setNewRect(clampedRect);
      }
    } else {
      const hoveredIndex = rectangles.findIndex((rect) => {
        const rectX1 = Math.min(rect.x, rect.x + rect.width);
        const rectX2 = Math.max(rect.x, rect.x + rect.width);
        const rectY1 = Math.min(rect.y, rect.y + rect.height);
        const rectY2 = Math.max(rect.y, rect.y + rect.height);
        return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
      });
      
      setHoveredRectIndex(hoveredIndex !== -1 ? hoveredIndex : null);
    }
  };

  const handleTouchEnd = (e) => {
    if (movingRectIndex !== null && tempRectTransform) {
      enableScroll();
      const updatedRectangles = editorRef.current.rectangles.map((rect, idx) => {
        if (idx === movingRectIndex) {
          const movedRect = {
            ...rect,
            x: rect.x + tempRectTransform.dx,
            y: rect.y + tempRectTransform.dy
          };
          return clampRectToImageBounds(movedRect);
        }
        return rect;
      });
      flushSync(() => {
        dispatchEditor({
          type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
          rectangles: updatedRectangles,
        });
      });
      SaveNativeHighlighter(updatedRectangles);
      setMovingRectIndex(null);
      setDragStart(null);
      setTempRectTransform(null);
      return;
    }
    
   
    if (resizingRectIndex !== null && tempRectTransform) {
      enableScroll();
      const updatedRectangles = editorRef.current.rectangles.map((rect, idx) => {
        if (idx === resizingRectIndex) {
          const { dx, dy } = tempRectTransform;
          const newRect = { ...rect };
         
          newRect.width = rect.width + dx;
          newRect.height = rect.height + dy;
         
          if (Math.abs(newRect.width) < 20) {
            newRect.width = rect.width;
          }
          if (Math.abs(newRect.height) < 20) {
            newRect.height = rect.height;
          }
         
          return clampRectToImageBounds(newRect);
        }
        return rect;
      });
      flushSync(() => {
        dispatchEditor({
          type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
          rectangles: updatedRectangles,
        });
      });
      SaveNativeHighlighter(updatedRectangles);
      setResizingRectIndex(null);
      setResizeHandle(null);
      setDragStart(null);
      setTempRectTransform(null);
      return;
    }
   
    if(!enableDrawerTool){
      return;
    }
    if (isDrawing) {
      enableScroll();
      if (newRect.width !== 0 && newRect.height !== 0) {
        const absWidth = Math.abs(newRect.width);
        const absHeight = Math.abs(newRect.height);
        if (absWidth >= 20 && absHeight >= 20) {
          const base = editorRef.current.rectangles;
          const updatedRectangles = [...base, newRect];
          flushSync(() => {
            dispatchEditor({
              type: 'PUSH_HISTORY_SET_RECTS_CLEAR_REDO',
              rectangles: updatedRectangles,
            });
          });
          SaveNativeHighlighter(updatedRectangles);
        }
      }
      setNewRect(null);
      setIsDrawing(false);
      return;
    }
   
    // Handle click/tap on rectangles and cross button (for iPad full screen)
    if (e && e.changedTouches && e.changedTouches.length > 0 && !isDrawing && movingRectIndex === null && resizingRectIndex === null) {
      const canvas = canvasRef.current;
      if (!canvas || !naturalDimensions.width || !naturalDimensions.height) return;
     
      const touch = e.changedTouches[0];
      const { x, y } = getTouchRelativePosition(touch);
      const item_index = checkContainerIndex(x, y);
      if (item_index < 0) {
        closeDefinitionTooltip();
        return;
      }
     
      const rect_item = rectangles[item_index];
      if (rect_item) {
        // Convert touch coordinates from natural dimensions to canvas pixel space
        const canvasX = (x / naturalDimensions.width) * canvas.width;
        const canvasY = (y / naturalDimensions.height) * canvas.height;
       
        let on_delete_icon = false;
        if (rect_item.crossButton && !enableMove && !enableResize) {
          on_delete_icon = isClickInside(canvasX, canvasY, rect_item.crossButton);
        }
        if (on_delete_icon && (enableDrawerTool || deleteOnIpad)) {
          handleDelete(item_index, rect_item);
          return;
        } else {
          handleRectangleClick(rect_item, item_index);
        }
      }
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color.hex);
    sessionStorage.setItem('selectedColor', color.hex);
  };

  const handleStrokeWidthChange = ({ key }) => {
    setStrokeWidth(Number(key));
    sessionStorage.setItem('strokeWidth', key);
  };

  const handleOpacityChange = (value) => {
    const minOpacity = Math.max(value / 100, 0.4);
    setOpacity(minOpacity);
    sessionStorage.setItem('opacity', minOpacity);
  };

  const handleModeChange = (checked) => {
    setIsHighlighterMode(checked);
    writeHighlighterModeForPage(bookId, pageId, checked);
    
    if (!disable_btns && !ebookFlagTeacherEdition && currentUser.role === 'student') {
      if (checked && !enableDrawerTool) {
        setEnableDrawerTool(true);
        setIsAnnotationsVisible(true);
        sessionStorage.setItem('isDrawingMode', true);
      }
    }
  };


  const handleUndo = () => {
    if (undoActionBusyRef.current) return;
    const s = editorRef.current;
    if (s.history.length === 0) {
      return;
    }
    undoActionBusyRef.current = true;
    requestAnimationFrame(() => {
      undoActionBusyRef.current = false;
    });
    flushSync(() => dispatchEditor({ type: 'UNDO' }));
    skipEbookRectanglesUntilRef.current = Date.now() + 4000;
    SaveNativeHighlighter(editorRef.current.rectangles);
  };

  const handleRedo = () => {
    if (redoActionBusyRef.current) return;
    const s = editorRef.current;
    if (s.redoHistory.length === 0) {
      return;
    }
    redoActionBusyRef.current = true;
    requestAnimationFrame(() => {
      redoActionBusyRef.current = false;
    });
    flushSync(() => dispatchEditor({ type: 'REDO' }));
    skipEbookRectanglesUntilRef.current = Date.now() + 4000;
    SaveNativeHighlighter(editorRef.current.rectangles);
  };

  const onRedoButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleRedo();
  };

  const onUndoButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleUndo();
  };


  const SaveNativeHighlighter = (rectangleData) => {
    try{
      let filterNativeData = [];
      if(Array.isArray(rectangleData)){
        filterNativeData = rectangleData?.filter((rect) => !rect.non_native) || [];
      }
      saveRectanglesToDB(filterNativeData);
    }catch(error){
      Sentry.captureException(`223 Error in SaveNativeHighlighter: ${error}`);
      console.log(error);
    }
  }

  useEffect(() => {
    if (ebookFlagTeacherEdition) {
      setIsAnnotationsVisible(false);
      setEnableHighLighterEditMode(false);
      setEnableDrawerTool(false);
      setEnableMove(false);
      setEnableResize(false);
      sessionStorage.setItem('isDrawingMode', false);
      sessionStorage.setItem('isMoveMode', false);
      sessionStorage.setItem('isResizeMode', false);
    } else {
      setIsAnnotationsVisible(true);
      setEnableHighLighterEditMode(true);
    }
  }, [ebookFlagTeacherEdition])

  useEffect(() => {
    if (!enableDrawerTool) {
      setCursorType('default');
    } else if (enableDrawerTool && !enableMove && !enableResize) {
      setCursorType('crosshair');
    }
  }, [enableDrawerTool, enableMove, enableResize])

  useEffect(() => {
    if (enableMove && enableResize) {
      setEnableResize(false);
      sessionStorage.setItem('isResizeMode', false);
    }
  }, [])

  const [showHideToogle,setShowHideToogle] = useState(false);

  const handleToggleAnnotations = async () => {
    if(ebookFlagTeacherEdition) return;
    setShowHideToogle(true);
    let filterNativeHighlter = [];
    if(Array.isArray(rectangles)){
      filterNativeHighlter = rectangles?.filter((rect) => !rect.non_native) || [];
    }
    setIsAnnotationsVisible(!isAnnotationsVisible);
    setEnableHighLighterEditMode(!enableHighLighterEditMode);
    await saveRectanglesToDB(filterNativeHighlter, !enableHighLighterEditMode);
    setShowHideToogle(false);
  };

  const handleDelete = (index) => {
    Modal.confirm({
      title: 'Delete Canvas',
      content: 'Are you sure you want to delete this Canvas?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        const cur = editorRef.current.rectangles;
        const updatedRectangles = cur.filter((_, i) => i !== index);
        flushSync(() => {
          dispatchEditor({
            type: 'PUSH_HISTORY_SET_RECTS',
            rectangles: updatedRectangles,
          });
        });
        SaveNativeHighlighter(updatedRectangles);
      }
    });
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: 'Clear All Highlights',
      content: 'Are you sure you want to clear all highlights? This action cannot be undone.',
      okText: 'Yes, Clear All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        const cur = editorRef.current.rectangles;
        let filterNonNativeHighlter = [];
        if (Array.isArray(cur)) {
          filterNonNativeHighlter = cur?.filter((rect) => rect.non_native) || [];
        }
        flushSync(() => {
          dispatchEditor({
            type: 'PUSH_HISTORY_SET_RECTS',
            rectangles: filterNonNativeHighlter,
          });
        });
        saveRectanglesToDB(filterNonNativeHighlter);
      }
    });
  };

  const saveRectanglesToDB = async (rectangles,show = enableHighLighterEditMode) => {
    try {
      let data = {
        rectangles,
        naturalDimensions
      };
      const dt = {
        user_id: currentUser.id,
        class_id: currentClass.id,
        page_id: pageId,
        book_id: bookId,
        data,
        show: show
      };
      const update_data = await dispatch(handleCreateEbookHighlights(dt));
      handleUpdateUserHighlights(update_data)
    } catch (error) {
      Sentry.captureException(`224 Error in saveRectanglesToDB: ${error}`);
      showAlert('Failed to save', "error");
    }
  };
  const handleUpdateUserHighlights = (update_data) => {
    if (update_data && update_data.id) {
      const uindex = userHighlites.findIndex(k => k.id == update_data.id)
      if (uindex >= 0) {
        userHighlites[uindex] = update_data
        setUserHighlites(userHighlites);
        setAllHighlightData([...videoHighliters,...userHighlites])
      }else{
        userHighlites.push(update_data)
        setUserHighlites(userHighlites);
        setAllHighlightData([...videoHighliters,...userHighlites])
      }
    }
  }
  const redrawCanvas = (naturalDimensions) => {
    const canvas = canvasRef.current;
    if (!canvas || !naturalDimensions?.width || !naturalDimensions?.height) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (isAnnotationsVisible && rectangles?.length  > 0) {
      rectangles.forEach((rect, index) => {
        let displayRect = { ...rect };
        
        if (tempRectTransform && tempRectTransform.index === index) {
          if (tempRectTransform.type === 'move') {
            displayRect.x = rect.x + tempRectTransform.dx;
            displayRect.y = rect.y + tempRectTransform.dy;
          } else if (tempRectTransform.type === 'resize') {
            const { dx, dy } = tempRectTransform;
            displayRect.width = rect.width + dx;
            displayRect.height = rect.height + dy;
          }
          displayRect = clampRectToImageBounds(displayRect);
        }
        
        if (displayRect.isHighlighterMode) {
          const alpha = Math.floor(displayRect.opacity * 255).toString(16).padStart(2, '0');
          ctx.fillStyle = `${displayRect.color}${alpha}`; 
          ctx.fillRect(
              (displayRect.x / naturalDimensions.width) * canvas.width,
              (displayRect.y / naturalDimensions.height) * canvas.height,
              (displayRect.width / naturalDimensions.width) * canvas.width,
              (displayRect.height / naturalDimensions.height) * canvas.height
          );
        } else {
         if (enableDrawerTool && enableMove && !displayRect.non_native) {
             ctx.strokeStyle = '#1890ff';
             ctx.lineWidth = 3;
             ctx.setLineDash([5, 5]);
         } else if (enableDrawerTool && enableResize && !displayRect.non_native) {
             ctx.strokeStyle = '#52c41a';
             ctx.lineWidth = 3;
             ctx.setLineDash([10, 5]);
         } else {
             ctx.strokeStyle = displayRect.color;
             ctx.lineWidth = displayRect.strokeWidth;
             ctx.setLineDash([]);
         }
         ctx.strokeRect(
             (displayRect.x / naturalDimensions.width) * canvas.width,
             (displayRect.y / naturalDimensions.height) * canvas.height,
             (displayRect.width / naturalDimensions.width) * canvas.width,
             (displayRect.height / naturalDimensions.height) * canvas.height
         );
         ctx.setLineDash([]);
     }
         const showCross = !displayRect.non_native && !enableMove && !enableResize && ((enableDrawerTool && hoveredRectIndex === index) || deleteOnIpad);
          if (showCross) {
          ctx.fillStyle = 'red';
          ctx.lineWidth = 100;
          const crossSize = 20;
          const rectX1 = Math.min(displayRect.x, displayRect.x + displayRect.width);
          const rectY1 = Math.min(displayRect.y, displayRect.y + displayRect.height);
          const crossX = (rectX1 / naturalDimensions.width) * canvas.width;
          const crossY = (rectY1 / naturalDimensions.height) * canvas.height;
          ctx.fillRect(crossX, crossY, crossSize, crossSize);
          ctx.fillStyle = 'white';
          ctx.font = '18px Arial';
          ctx.fillText('X', crossX + crossSize / 4, crossY + (crossSize * 3) / 4);

          rect.crossButton = {
            x: crossX,
            y: crossY,
            width: crossSize,
            height: crossSize
        };
        } else if (!displayRect.non_native) {
          rect.crossButton = null;
        }

      });
    }

    if (newRect) {
      if (newRect.isHighlighterMode) {
        const alpha = Math.floor(newRect.opacity * 255).toString(16).padStart(2, '0');
        const fillStyle = `${newRect.color}${alpha}`; 
        ctx.fillStyle = fillStyle;
        ctx.fillRect(
          (newRect.x / naturalDimensions?.width) * canvas?.width,
          (newRect.y / naturalDimensions?.height) * canvas?.height,
          (newRect.width / naturalDimensions?.width) * canvas?.width,
          (newRect.height / naturalDimensions?.height) * canvas?.height
        );
      } else {
        ctx.strokeStyle = newRect.color;
        ctx.lineWidth = newRect.strokeWidth;
        ctx.strokeRect(
          (newRect.x / naturalDimensions?.width) * canvas?.width,
          (newRect.y / naturalDimensions?.height) * canvas?.height,
          (newRect.width / naturalDimensions?.width) * canvas?.width,
          (newRect.height / naturalDimensions?.height) * canvas?.height
        );
      }
    }
  };

  const updateTooltipPosition = (rectIndex) => {
    if (rectIndex !== null && rectIndex >= 0 && rectangles[rectIndex]) {
      const rect = rectangles[rectIndex];
      const canvas = canvasRef.current;
      const image = imageRef.current;
      if (canvas && image && naturalDimensions.width && naturalDimensions.height) {
        const rectX = Math.min(rect.x, rect.x + rect.width);
        const rectY = Math.min(rect.y, rect.y + rect.height);
        const rectWidth = Math.abs(rect.width);
        
        const centerX = rectX + (rectWidth / 2);
        const screenX = (centerX / naturalDimensions.width) * image.clientWidth;
        const screenY = (rectY / naturalDimensions.height) * image.clientHeight;
        
        const containerRect = image.getBoundingClientRect();
        setTooltipPosition({
          x: containerRect.left + screenX,
          y: containerRect.top + screenY
        });
      }
    }
  };

  const openDefinitionTooltip = (rectIndex) => {
    if (rectIndex !== null && rectIndex >= 0 && rectangles[rectIndex]?.definition && rectangles[rectIndex].definition.trim()) {
      const rect = rectangles[rectIndex];
      setDefinitionText(rect.definition);
      setDefinitionWord(rect.word || '');
      setOpenTooltipIndex(rectIndex);
      updateTooltipPosition(rectIndex);
    }
  };

  const closeDefinitionTooltip = () => {
    setDefinitionText('');
    setDefinitionWord('');
    setOpenTooltipIndex(null);
  };

  useLayoutEffect(() => {
    redrawCanvas(naturalDimensions);
  }, [rectangles, newRect, isAnnotationsVisible, hoveredRectIndex, enableDrawerTool, enableMove, enableResize, tempRectTransform, deleteOnIpad, naturalDimensions]);

  useEffect(() => {
    if (openTooltipIndex !== null) {
      const handleScroll = () => {
        updateTooltipPosition(openTooltipIndex);
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [openTooltipIndex, rectangles, naturalDimensions]);

  useEffect(() => {
    if (!definitionText) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ant-popover')) {
        closeDefinitionTooltip();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [definitionText]);

  const strokeWidthMenu = {
    items: [2, 3, 4, 5, 6, 7, 8].map((width) => ({
      key: width,
      label: <LineWidthPreview strokeWidth={width} top={5} className='stock-lebel' color="black" />
    })),
    onClick: handleStrokeWidthChange
  };

  const handleMouseLeave = () => {
    setHoveredRectIndex(null);
    setCursorType('default');
  };

  const handleRectangleClick = (rectangle, index) => {
    if(rectangle?.audioFile){
      setOpenAudioPlayer(true);
      setAudioUrl(rectangle?.audioFile);
    }else if(rectangle?.videoFile){
      setVideoModal(true);
      setVideoUrl(rectangle.videoFile);
    }else if(rectangle?.definition && rectangle.definition.trim()){
      if(openTooltipIndex === index){
        closeDefinitionTooltip();
      } else {
        openDefinitionTooltip(index);
      }
    }
};

// Main click handler
const handleCanvasClick = (e) => {
  e.preventDefault();
  
  const canvas = canvasRef.current;
  if (!canvas || !naturalDimensions.width || !naturalDimensions.height) return;

  // Get coordinates in natural dimensions space
  const { x, y } = getRelativePosition(e);
  const item_index = checkContainerIndex(x, y)
  if (item_index < 0) {
    closeDefinitionTooltip();
    return;
  }
  const rect_item = rectangles[item_index]
  if (rect_item) {
    const canvasX = (x / naturalDimensions.width) * canvas.width;
    const canvasY = (y / naturalDimensions.height) * canvas.height;
    
    let on_delete_icon = false;
    if (rect_item.crossButton && !enableMove && !enableResize) {
      on_delete_icon = isClickInside(canvasX, canvasY, rect_item.crossButton)
    }
    if (on_delete_icon && (enableDrawerTool || deleteOnIpad)) {
      handleDelete(item_index, rect_item);
      return;
    } else {
      handleRectangleClick(rect_item, item_index);
    }
  }
};

  const isClickInside = (x, y, button) => {
    return x >= button.x &&
           x <= button.x + button.width &&
           y >= button.y &&
           y <= button.y + button.height;
};

const handleClose = (type) => {
    if(type === "video"){
        setVideoModal(false);
    } else{
        setOpenAudioPlayer(false);
    }

}

const handleMouseOutForCanvas = () => {
  if(isDrawing){
      setNewRect(null);
      setIsDrawing(false);
      enableScroll();
  }
  if (movingRectIndex === null && resizingRectIndex === null) {
    setCursorType('default');
  }
}
const disable_btns = currentUser.role=='admin';
const filterNativeRectangles = useMemo(() => rectangles?.filter((rect) => !rect.non_native), [rectangles]);

useEffect(() => {
  if(filterNativeRectangles?.length === 0){
    setEnableResize(false);
    setEnableMove(false);
    sessionStorage.setItem('isResizeMode', false);
    sessionStorage.setItem('isMoveMode', false);
  }
}, [filterNativeRectangles]);

const handleImageError = async () => {
  try {
    if (!imageUrl) return;

    if (navigator.onLine === false) {
      return;
    }

    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      if (response.status === 404) {
        Sentry.captureException(new Error(`Image not found: ${imageUrl}`), {
          tags: { errorType: 'image_not_found' },
          extra: { imageUrl, pageId, bookId }
        });
      } else if (response.status >= 400 && response.status < 500) {
        Sentry.captureException(new Error(`Image access error (${response.status}): ${imageUrl}`), {
          tags: { errorType: 'image_access_error', statusCode: response.status },
          extra: { imageUrl, pageId, bookId }
        });
      } else if (response.status >= 500) {
        Sentry.captureException(new Error(`Server error loading image (${response.status}): ${imageUrl}`), {
          tags: { errorType: 'server_error', statusCode: response.status },
          extra: { imageUrl, pageId, bookId }
        });
      }
    } catch (fetchError) {
      if (fetchError.name === 'TypeError' && (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('CORS') || fetchError.message.includes('cross-origin'))) {
        try {
          if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
          const url = new URL(imageUrl);
            if (url.origin && url.origin !== 'null') {
          Sentry.captureException(new Error(`Domain not accessible: ${url.origin}`), {
            tags: { errorType: 'domain_not_accessible' },
            extra: { imageUrl, pageId, bookId, origin: url.origin }
          });
            }
          }
        } catch (urlError) {
        }
      }
    }
  } catch (error) {
    if (error.name !== 'AbortError' && !error.message?.toLowerCase().includes('network') && !error.message?.toLowerCase().includes('cancel')) {
      Sentry.captureException(new Error(`Image load error: ${imageUrl}`), {
        tags: { errorType: 'image_load_error' },
        extra: { imageUrl, pageId, bookId, error: error.message }
      });
    }
  }
};

  return (
  <SentryWrap>
    <ImageHighlighterDefinitionPopoverStyle />
    <DefaultVideoPlayer openVideoModal={openVideoModal} handleClose={() => handleClose("video")} video={videoUrl} />
    <DefaultAudioPlayer openAudioPlayer={openAudioPlayer} handleClose={()=> handleClose("audio")} audio={audioUrl} />
      {definitionText && (
        <Popover
          content={
            <div style={{ maxHeight: '150px', overflowY: 'auto', maxWidth: '300px' }}>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{definitionText}</p>
            </div>
          }
          title={
            <div>
              {definitionWord && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: '-8px' }}>
                  <span><label>Word: </label><span style={{ fontWeight: 400, fontSize: 14 }}>{definitionWord}</span></span>
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={closeDefinitionTooltip}
                    style={{
                      padding: '0 4px',
                      minWidth: 'auto',
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  />
                </div>
              )}
              <div>
                <span>Definition</span>
              </div>
            </div>
          }
          open={!!definitionText}
          placement="top"
          getPopupContainer={() => document.body}
          overlayClassName="image-highlighter-definition-popover"
        >
          <div 
            style={{ 
              position: 'fixed', 
              left: `${tooltipPosition.x}px`, 
              top: `${tooltipPosition.y}px`, 
              width: 1, 
              height: 1,
              pointerEvents: 'none',
              zIndex: 9999
            }} 
          />
        </Popover>
      )}
        <Container className="container to_be_print" style={{ position: 'relative' }}>
      <Image
        ref={imageRef}
        src={imageUrl}
        alt="Ebook page"
        className="image to_be_print"
        loading="eager"
        decode="sync"
        fetchPriority="high"
        onLoad={() => {
          const canvas = canvasRef.current;
          const image = imageRef.current;
          if (canvas && image && image.naturalWidth && image.naturalHeight) {
            setIsImageLoaded(true);
            const newNaturalDimensions = {
              width: image.naturalWidth,
              height: image.naturalHeight,
            };
            setImageConfiguration({
              width: image.naturalWidth,
              height: image.naturalHeight,
              clientWidth: image.clientWidth,
              clientHeight: image.clientHeight,
            });
            setNaturalDimensions(newNaturalDimensions);
            canvas.width = image.clientWidth;
            canvas.height = image.clientHeight;
            redrawCanvas(newNaturalDimensions);
          }
        }}
        onError={handleImageError}
      />
        <CanvasContainer
        ref={canvasRef}
        cursorType={cursorType}
        className="highlight-canvas canvasAutoHeight"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClick={handleCanvasClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseLeave={handleMouseLeave}
        onMouseOut={handleMouseOutForCanvas}
      />
      {is_image_loaded ? (
          <ControlsContainer>
            <Popover
              content={<SketchPicker color={selectedColor} onChange={handleColorChange} />}
              title="Select Color"
              trigger="click"
            >
              <Tooltip title="Pick Color">
                <Button
                  disabled={disable_btns}
                  shape="circle"
                  style={{
                    backgroundColor: selectedColor,
                    border: '1px solid #ccc',
                    width: 32,
                    height: 32,
                    padding: 0,
                  }}
                />
              </Tooltip>
            </Popover>

            <Dropdown menu={strokeWidthMenu} trigger={['click']} disabled={disable_btns}>
              <Tooltip title="Stroke Width">
                <Button shape="circle" style={{ width: 32, height: 32 }}>
                  <LineWidthPreview strokeWidth={strokeWidth} top={0} color="black" />
                </Button>
              </Tooltip>
            </Dropdown>

            <Tooltip title="Toggle Highlighter / Outline">
              <Switch
                checked={isHighlighterMode}
                disabled={disable_btns}
                onChange={handleModeChange}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                checkedChildren="Highlighter"
                unCheckedChildren="Outline"
              />
            </Tooltip>

            {isHighlighterMode && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>Opacity</span>
                <Slider
                  value={opacity * 100}
                  min={40}
                  onChange={handleOpacityChange}
                  style={{ width: 100 }}
                  disabled={disable_btns}
                />
              </div>
            )}

            <Tooltip title="Undo">
              <Button htmlType="button" icon={<UndoOutlined />} disabled={disable_btns} onClick={onUndoButtonClick} />
            </Tooltip>

            <Tooltip title="Redo">
              <Button htmlType="button" icon={<RedoOutlined />} disabled={disable_btns} onClick={onRedoButtonClick} />
            </Tooltip>

            <Tooltip title={isAnnotationsVisible ? "Hide Highlights" : "Show Highlights"}>
              <Button
                disabled={disable_btns || showHideToogle}
                icon={
                  showHideToogle ? (
                    <Loading size={13} />
                  ) : isAnnotationsVisible ? (
                    <EyeOutlined />
                  ) : (
                    <EyeInvisibleOutlined />
                  )
                }
                onClick={handleToggleAnnotations}
              />
            </Tooltip>

            <Tooltip title={enableDrawerTool ? "Disable Drawing" : "Enable Drawing"}>
              <Switch
                checked={enableDrawerTool}
                disabled={disable_btns || ebookFlagTeacherEdition}
                onChange={(val) => {
                  if(val){
                    setIsAnnotationsVisible(true);
                  } else {
                    setEnableMove(false);
                    setEnableResize(false);
                    setIsHighlighterMode(false);
                    writeHighlighterModeForPage(bookId, pageId, false);
                    sessionStorage.setItem('isMoveMode', 'false');
                    sessionStorage.setItem('isResizeMode', 'false');
                  }
                  setEnableDrawerTool(val);
                  sessionStorage.setItem('isDrawingMode', val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                checkedChildren="Draw On"
                unCheckedChildren="Draw Off"
              />
            </Tooltip>

            <Tooltip title={enableMove ? "Disable Moving" : "Enable Moving"}>
              <Switch
                checked={enableMove}
                disabled={disable_btns || ebookFlagTeacherEdition || !enableDrawerTool || filterNativeRectangles?.length === 0}
                onChange={(val) => {
                  if(val){
                    setIsAnnotationsVisible(true);
                    setEnableResize(false);
                    sessionStorage.setItem('isResizeMode', false);
                    setDeleteOnIpad(false);
                  }
                  setEnableMove(val);
                  sessionStorage.setItem('isMoveMode', val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                checkedChildren="Move On"
                unCheckedChildren="Move Off"
              />
            </Tooltip>

            <Tooltip title={enableResize ? "Disable Resizing" : "Enable Resizing"}>
              <Switch
                checked={enableResize}
                disabled={disable_btns || ebookFlagTeacherEdition || !enableDrawerTool || filterNativeRectangles?.length === 0}
                onChange={(val) => {
                  if(val){
                    setIsAnnotationsVisible(true);
                    setEnableMove(false);
                    sessionStorage.setItem('isMoveMode', false);
                    setDeleteOnIpad(false);
                  }
                  setEnableResize(val);
                  sessionStorage.setItem('isResizeMode', val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                checkedChildren="Resize On"
                unCheckedChildren="Resize Off"
              />
            </Tooltip>

            <Tooltip title={deleteOnIpad ? "Disable Delete" : "Enable Delete"}>
              <Switch
                checked={deleteOnIpad}
                disabled={enableMove || enableResize}
                onChange={(val) => {
                  setDeleteOnIpad(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                checkedChildren="Delete off"
                unCheckedChildren="Delete On"
              />
            </Tooltip>

            <Tooltip title="Clear All Highlights">
              <Button
                disabled={disable_btns || filterNativeRectangles?.length === 0}
                icon={<DeleteOutlined />}
                onClick={handleClearAll}
              />
            </Tooltip>
          </ControlsContainer>
      ) : null}
    </Container>
  </SentryWrap>
  );
};

export default ImageHighlighter;
